/**
 * VersionChip — small label showing the current SitRep or IAP version.
 * Click to see the full version history in a modal; click a past version
 * to view that snapshot read-only.
 */
import React, { useEffect, useState } from 'react';
import { API_BASE, getApiHeaders } from '../api/apiClient';

interface VersionRow {
  id:            number;
  version:       string;
  major:         number;
  minor:         number;
  triggeredBy:   string;
  meetingId:     number | null;
  createdBy:     string | null;
  createdAt:     string;
  signedOffName?: string | null;
  signedOffAt?:   string | null;
}

type Kind = 'sitrep' | 'iap';

async function fetchJson<T>(url: string): Promise<T> {
  const res  = await fetch(url, { headers: getApiHeaders() });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as T;
}

export function VersionChip({ kind, incidentId }: { kind: Kind; incidentId: string }) {
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [showList, setShowList] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);

  const endpoint = kind === 'sitrep' ? 'sitrep' : 'iap';
  const label    = kind === 'sitrep' ? 'SitRep' : 'IAP';

  async function reload() {
    setLoading(true);
    try {
      const d = await fetchJson<{ versions: VersionRow[] }>(
        `${API_BASE}/cima/incidents/${incidentId}/${endpoint}/versions`,
      );
      setVersions(d.versions);
      setError('');
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [incidentId, endpoint]);

  // The chip's `latest` was the agent's v1.0 reading even after the
  // backend had moved on to v1.2 (Yash hit this when posting loop-2
  // updates — chip showed v1.0, modal showed v1.2). Poll every 6 s so
  // the chip stays in sync with version writes triggered elsewhere
  // (agent tool calls, IAP regen, meeting notes pull). Suspend while
  // the modal is open since clicking already triggers a reload.
  useEffect(() => {
    if (showList) return;
    const t = setInterval(() => { reload(); }, 6000);
    return () => clearInterval(t);
  }, [incidentId, endpoint, showList]);

  const latest = versions[versions.length - 1];

  // Previous behaviour: hide entirely when there are no versions yet. That
  // caused the demo-day confusion — operators opened a freshly-created
  // incident and assumed versioning was broken because no chip showed.
  // New behaviour: always render the chip; show "— (no versions yet)"
  // when empty so the affordance is visible.

  return (
    <>
      <button
        onClick={() => { setShowList(true); reload(); }}
        title={latest
          ? `${label} version — click for history`
          : loading
            ? `Loading ${label} versions…`
            : `No ${label} versions yet — will appear once the agent / operator updates the ${label}.`}
        style={{
          background: kind === 'sitrep' ? 'rgba(30,64,175,0.12)' : 'rgba(13,148,136,0.12)',
          color:      kind === 'sitrep' ? '#1e40af'              : '#0d9488',
          border:     `1px solid ${kind === 'sitrep' ? 'rgba(30,64,175,0.35)' : 'rgba(13,148,136,0.35)'}`,
          borderRadius: 999,
          padding: '3px 10px',
          fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 5,
          opacity: latest ? 1 : 0.65,
        }}
      >
        {label} v{latest ? latest.version : (loading ? '…' : '—')}
        <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
      </button>

      {showList && (
        <div
          onClick={() => { setShowList(false); setViewingId(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1003,
            background: 'rgba(12,12,18,0.58)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'zipModalFadeIn 180ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)', border: '1px solid var(--border-md)',
              borderRadius: 14, padding: '22px 24px', maxWidth: 560, width: '92%',
              maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>
                {label} version history
              </h3>
              <button
                onClick={() => { setShowList(false); setViewingId(null); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer', padding: 0 }}
              >×</button>
            </div>
            {error && <p style={{ color: 'var(--accent-red)', fontSize: 12 }}>⚠ {error}</p>}
            {versions.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: 13 }}>No versions yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...versions].reverse().map((v) => {
                  const isLatest = v.id === latest?.id;
                  return (
                    <div
                      key={v.id}
                      style={{
                        background: isLatest ? 'rgba(74,222,128,0.08)' : 'var(--surface)',
                        border: `1px solid ${isLatest ? 'rgba(74,222,128,0.35)' : 'var(--border)'}`,
                        borderRadius: 8, padding: '10px 12px',
                      }}
                    >
                      <div
                        onClick={() => setViewingId(v.id === viewingId ? null : v.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>
                          v{v.version}
                        </span>
                        {isLatest && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', background: 'rgba(74,222,128,0.18)', padding: '1px 6px', borderRadius: 4, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                            current
                          </span>
                        )}
                        {v.signedOffAt && (
                          <span
                            title={`Signed by ${v.signedOffName || 'unknown'} at ${new Date(v.signedOffAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })}`}
                            style={{ fontSize: 9, fontWeight: 700, color: '#0d9488', background: 'rgba(13,148,136,0.15)', padding: '1px 6px', borderRadius: 4, letterSpacing: 0.4, textTransform: 'uppercase' }}
                          >
                            ✓ signed
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {v.triggeredBy}{v.createdBy ? ` · ${v.createdBy}` : ''}
                        </span>
                        {/* Right-anchored cluster — date + per-version
                            download grouped together so the button stays
                            visible regardless of how the row wraps. The
                            previous layout used marginLeft:auto on just
                            the date, which pushed the button off the
                            right edge on narrow modals. */}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--subtle)' }}>
                            {new Date(v.createdAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', dateStyle: 'medium', timeStyle: 'short' })}
                          </span>
                          {kind === 'sitrep' && (
                            <>
                              <ViewHtmlReportButton incidentId={incidentId} />
                              <DownloadVersionButton
                                incidentId={incidentId}
                                versionId={v.id}
                                label={v.version}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      {viewingId === v.id && (
                        <VersionDetail
                          kind={kind}
                          incidentId={incidentId}
                          versionId={v.id}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Pulls a SitRep version as a full incident XLSX and triggers a browser
 * download. The XLSX uses the standard incident template — CIMT contacts,
 * IAP, Stakeholders pages — but the SitRep page is filled from the
 * frozen snapshot for that version. So historical v1.0 always shows
 * v1.0's facts/assumptions/etc., even after the live SitRep moves on.
 *
 * We can't use a plain <a download> because the request needs the
 * X-User-Email header (auth), so we fetch with apiClient headers,
 * convert to a Blob, and click an in-memory <a> element.
 */
function DownloadVersionButton({ incidentId, versionId, label }: { incidentId: string; versionId: number; label: string }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // don't toggle the snapshot panel
    setBusy(true);
    try {
      // Backend returns { filename, data (base64), contentType } JSON —
      // not raw binary — because API Gateway mangles binary responses.
      // Same decode pattern as the existing /sitrep/export ZIP flow.
      const res = await fetch(
        `${API_BASE}/cima/incidents/${incidentId}/sitrep/versions/${versionId}/download`,
        { headers: getApiHeaders() },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const binaryStr = atob(json.data);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: json.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = json.filename || `SitRep_${incidentId}_v${label}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      title={`Download v${label} as XLSX (full template — SitRep page reflects this version)`}
      style={{
        background: 'var(--primary)', color: 'var(--on-primary)',
        border: 'none', borderRadius: 6, padding: '4px 10px',
        cursor: busy ? 'wait' : 'pointer',
        fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        opacity: busy ? 0.7 : 1, whiteSpace: 'nowrap',
      }}
    >
      {busy ? 'Building…' : (<>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        XLSX
      </>)}
    </button>
  );
}

/**
 * Sibling to DownloadVersionButton — opens the modern HTML incident
 * report in a new tab. Click = view in browser; Shift-click = download
 * as a self-contained .html file. Lives next to every XLSX download
 * spot so users always have a polished web-friendly alternative to the
 * spreadsheet.
 *
 * Uses the LIVE state for the report (not a frozen version snapshot —
 * the HTML report's intent is "see the current state, polished" rather
 * than "audit a past version" which is what the XLSX-by-version is for).
 */
function ViewHtmlReportButton({ incidentId }: { incidentId: string }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const url = `${API_BASE}/cima/incidents/${incidentId}/sitrep/report.html${e.shiftKey ? '?download=1' : ''}`;
      const res = await fetch(url, { headers: getApiHeaders() });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      if (e.shiftKey) {
        const filename = (res.headers.get('content-disposition') || '').match(/filename="([^"]+)"/)?.[1]
          || `IncidentReport_${incidentId}.html`;
        const a = document.createElement('a');
        a.href = objectUrl; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      } else {
        window.open(objectUrl, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      alert(err.message || 'Failed to open HTML report');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      title="Open the modern HTML report in a new tab (Shift-click to download as a file)"
      style={{
        background: 'var(--primary-soft, #F3EBFC)',
        color: 'var(--primary, #0067FE)',
        border: '1px solid var(--primary-border, #D6C0EC)',
        borderRadius: 6, padding: '4px 10px',
        cursor: busy ? 'wait' : 'pointer',
        fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        opacity: busy ? 0.7 : 1, whiteSpace: 'nowrap',
      }}
    >
      {busy ? '…' : (<>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
        </svg>
        HTML
      </>)}
    </button>
  );
}

// ── Pretty snapshot renderer ──────────────────────────────────────────────
// Turns a raw version snapshot object into a readable, sectioned view instead
// of dumping JSON. Generic (handles any shape) so it works for both SitRep and
// IAP snapshots regardless of which fields are present.
function humanizeKey(k: string): string {
  return k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')  // camelCase → words
    .replace(/[_-]+/g, ' ')                    // snake / kebab → spaces
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
}

function isPlainObject(v: any): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isEmptyVal(v: any): boolean {
  return v === null || v === undefined || v === ''
    || (Array.isArray(v) && v.length === 0)
    || (isPlainObject(v) && Object.keys(v).length === 0);
}

// A "flat" object is a plain object whose every value is a primitive — e.g. the
// severity ratings (domain → rating). These read best as a 2-column grid.
function isFlatObject(v: any): boolean {
  return isPlainObject(v) && Object.values(v).every((x) => x === null || typeof x !== 'object');
}

// Label / value rows for a plain object (fact, action, rating, …). Set
// table for flat key→value maps like severity ratings — renders an aligned
// two-column table (labels in one column, values equidistant in the next).
function KeyValueRows({ obj, table = false }: { obj: Record<string, any>; table?: boolean }) {
  const entries = Object.entries(obj).filter(([, v]) => !isEmptyVal(v));
  if (entries.length === 0) return <span style={{ color: 'var(--subtle)' }}>—</span>;

  const labelStyle: React.CSSProperties = {
    fontSize: 9.5, fontWeight: 700, color: 'var(--muted)',
    textTransform: 'uppercase', letterSpacing: 0.3,
  };

  if (table) {
    // Aligned label | value grid: `max-content` sizes the label column to the
    // widest label so every value lines up in a single, equidistant column.
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 24, rowGap: 5, alignItems: 'baseline' }}>
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={labelStyle}>{humanizeKey(k)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg)' }}>
              {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {entries.map(([k, v]) => {
        const nested = Array.isArray(v) || isPlainObject(v);
        return (
          <div key={k} style={nested ? {} : { display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ ...labelStyle, flexShrink: 0, minWidth: nested ? undefined : 80 }}>
              {humanizeKey(k)}
            </span>
            <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--fg)' }}>
              <PrettyValue value={v} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Renders any JSON value as a readable block (bulleted list / numbered cards /
// key-value rows / plain text) — never raw JSON.
function PrettyValue({ value }: { value: any }): JSX.Element {
  if (isEmptyVal(value)) return <span style={{ color: 'var(--subtle)' }}>—</span>;

  if (Array.isArray(value)) {
    const allPrimitive = value.every((x) => x === null || typeof x !== 'object');
    if (allPrimitive) {
      return (
        <ul style={{ margin: '2px 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {value.map((x, i) => <li key={i} style={{ color: 'var(--fg)' }}>{String(x)}</li>)}
        </ul>
      );
    }
    // Array of objects → one numbered card per entry.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
        {value.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--subtle)', fontFamily: 'var(--font-mono)', flexShrink: 0, minWidth: 16 }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isPlainObject(item) ? <KeyValueRows obj={item} /> : <span style={{ color: 'var(--fg)' }}>{String(item)}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) return <KeyValueRows obj={value} />;
  if (typeof value === 'boolean') return <span style={{ color: 'var(--fg)' }}>{value ? 'Yes' : 'No'}</span>;
  return <span style={{ color: 'var(--fg)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(value)}</span>;
}

function VersionDetail({ kind, incidentId, versionId }: { kind: Kind; incidentId: string; versionId: number }) {
  const [data, setData] = useState<any | null>(null);
  const [err, setErr]   = useState('');
  useEffect(() => {
    setData(null); setErr('');
    const ep = kind === 'sitrep' ? 'sitrep' : 'iap';
    fetchJson<any>(`${API_BASE}/cima/incidents/${incidentId}/${ep}/versions/${versionId}`)
      .then((d) => setData(d.version))
      .catch((e) => setErr(e.message));
  }, [incidentId, versionId, kind]);

  if (err)   return <div style={{ color: 'var(--accent-red)', fontSize: 11, marginTop: 6 }}>⚠ {err}</div>;
  if (!data) return <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>Loading snapshot…</div>;

  const payload = kind === 'sitrep' ? data.snapshot : data.content;
  const sections = isPlainObject(payload)
    ? Object.entries(payload).filter(([, v]) => !isEmptyVal(v))
    : [];
  return (
    <div style={{
      marginTop: 10, padding: '12px 14px', background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 8, fontSize: 12.5, color: 'var(--fg)', maxHeight: 300, overflow: 'auto',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {isPlainObject(payload) ? (
        sections.length === 0
          ? <span style={{ color: 'var(--subtle)', fontStyle: 'italic' }}>No details recorded in this version.</span>
          : sections.map(([section, val]) => (
              <div key={section}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                  {humanizeKey(section)}
                </div>
                {isFlatObject(val)
                  ? <KeyValueRows obj={val as Record<string, any>} table />
                  : <PrettyValue value={val} />}
              </div>
            ))
      ) : (
        <PrettyValue value={payload} />
      )}
    </div>
  );
}
